import { Box, Stack, TextField, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";

import {
  AutoCompleteUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
  ModalFilters,
} from "@4uhub/lib4uhub";

export interface INonLabFilterParams {
  encounterNumber: string;
  prescriptionNumber: string;
  prescriptionProcNumber: string;
  prescriptionNumberOS: string;
  prescriptionProcNumberOS: string;
  encounterNumberOS: string;
  createdStart: Date | null;
  createdEnd?: Date | null;
  status: string;
}

type TStringField<T> = {
  [K in keyof T]: T[K] extends string ? K : never;
}[keyof T];

interface INonLabMacroFilterProps<T extends INonLabFilterParams> {
  onFilterParams: Dispatch<SetStateAction<T>>;
  filterParams: T;
  /** Translation path of the screen, used for the field labels */
  translationPath: string;
  /** Patient document field, which differs between the screens */
  documentField: TStringField<T>;
}

const sharedTranslationPath = "page.4uBus.logs.tasyToTasyNonLabFilters.";

/** Fields shown inside the "more filters" modal, grouped by origin */
const LOCAL_FIELDS = [
  { field: "prescriptionNumber", label: "prescription_number" },
  { field: "prescriptionProcNumber", label: "procedure_prescription_number" },
] as const;

const EXTERNAL_FIELDS = [
  { field: "encounterNumberOS", label: "external_service_number" },
  { field: "prescriptionNumberOS", label: "external_prescription_number" },
  {
    field: "prescriptionProcNumberOS",
    label: "external_procedure_prescription_number",
  },
] as const;

const ADVANCED_FIELDS = [...LOCAL_FIELDS, ...EXTERNAL_FIELDS];

const mainFieldSx = { flex: "1 1 180px", minWidth: 160 };

const NonLabMacroFilter = <T extends INonLabFilterParams>({
  onFilterParams,
  filterParams,
  translationPath,
  documentField,
}: INonLabMacroFilterProps<T>) => {
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);

  const { t } = useTranslation();

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const [selectedStatus, setSelectedStatus] = useState<ISelectType | null>(
    null,
  );

  const [resetDateRange, setResetDateRange] = useState<boolean>(false);
  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setSelectedStatus(null);
    setResetDateRange((prev) => !prev);
  };

  const [enableFields, setEnableFields] = useState<boolean>(true);

  const handleStartDate = (date: Date | null) => {
    setFilter({ createdStart: date } as Partial<T>);
  };
  const handleEndDate = (date: Date | null) => {
    setFilter({ createdEnd: date } as Partial<T>);
  };

  const handleSelectStatus = useCallback(
    (value: ISelectType | null) => {
      setFilter({ status: value?.id ?? "" } as Partial<T>);
      setSelectedStatus(value);
    },
    [setFilter],
  );

  const badgeCount = useMemo(
    () =>
      ADVANCED_FIELDS.filter(
        ({ field }) => filterParams[field] !== initialState.current[field],
      ).length,
    [filterParams, initialState],
  );

  const statuses: ISelectType[] = useMemo(() => {
    return [
      {
        id: "DISPONIVEL",
        name: t(translationPath + "status.available"),
        code: "D",
      },
      {
        id: "RECONHECIDO",
        name: t(translationPath + "status.recognized"),
        code: "R",
      },
      {
        id: "DOWNLOAD COMPLETO",
        name: t(translationPath + "status.downloaded"),
        code: "DC",
      },
      {
        id: "INTEGRADO",
        name: t(translationPath + "status.integrated"),
        code: "I",
      },
      {
        id: "ERRO",
        name: t(translationPath + "status.error"),
        code: "E",
      },
      {
        id: "CANCELADO",
        name: t(translationPath + "status.cancelled"),
        code: "C",
      },
    ];
  }, [t, translationPath]);

  const renderField = (
    field: (typeof ADVANCED_FIELDS)[number]["field"],
    label: string,
  ) => (
    <TextField
      key={field}
      value={filter[field]}
      size="small"
      label={t(translationPath + label)}
      fullWidth
      onChange={(e) => setFilter({ [field]: e.target.value } as Partial<T>)}
    />
  );

  return (
    <Stack
      flexDirection="row"
      gap={1.5}
      alignItems="center"
      flexWrap="wrap"
      sx={{ width: "100%" }}
    >
      <DateRangeSelect
        setEndDate={handleEndDate}
        setStartDate={handleStartDate}
        setEnableFields={setEnableFields}
        resetToToday={resetDateRange}
      />

      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.createdStart}
          onChange={(e) => setFilter({ createdStart: e } as Partial<T>)}
          label={`${t(translationPath + "start_date")}`}
          isClearable={false}
          disable={enableFields}
        />
      </Box>

      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.createdEnd}
          onChange={(e) => setFilter({ createdEnd: e } as Partial<T>)}
          label={`${t(translationPath + "end_date")}`}
          isClearable={false}
          disable={enableFields}
        />
      </Box>

      <Box sx={mainFieldSx}>
        <TextField
          value={filter.encounterNumber}
          size="small"
          label={t(translationPath + "service_number")}
          fullWidth
          onChange={(e) =>
            setFilter({ encounterNumber: e.target.value } as Partial<T>)
          }
        />
      </Box>

      <Box sx={mainFieldSx}>
        <TextField
          value={filter[documentField]}
          size="small"
          label={t(translationPath + "patient_cpf")}
          fullWidth
          onChange={(e) =>
            setFilter({ [documentField]: e.target.value } as Partial<T>)
          }
        />
      </Box>

      <Box sx={{ flex: "1 1 160px", minWidth: 140 }}>
        <AutoCompleteUncontrolled
          label={t(translationPath + "status_item") || "Status"}
          onValueSelected={handleSelectStatus}
          value={selectedStatus}
          options={statuses}
          size="small"
          fullWidth
        />
      </Box>

      <FiltersActions
        onFilter={filterHandler}
        onClean={cleanHandler}
        showCleanBtn={showCleanBtn}
      />

      <ModalFilters
        badgeCount={badgeCount}
        actionsDisabled={false}
        onFilter={filterHandler}
        onCleanFilter={cleanHandler}
        triggerVariant="button"
        triggerLabel={
          t(sharedTranslationPath + "additional_filters") || "Mais filtros"
        }
        triggerButtonVariant="contained"
      >
        <Stack gap={1.5} sx={{ width: "100%" }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t(sharedTranslationPath + "local_group")}
          </Typography>
          {LOCAL_FIELDS.map(({ field, label }) => renderField(field, label))}

          <Typography variant="subtitle2" color="text.secondary" mt={1}>
            {t(sharedTranslationPath + "external_group")}
          </Typography>
          {EXTERNAL_FIELDS.map(({ field, label }) => renderField(field, label))}
        </Stack>
      </ModalFilters>
    </Stack>
  );
};

export default NonLabMacroFilter;
